import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import * as jobsApi from '@/api/jobs.api';
import * as contactsApi from '@/api/contacts.api';
import * as channelsApi from '@/api/channels.api';
import * as installationApi from '@/api/installation.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { CanalDelClienteSelect } from '@/components/contacts/CanalDelClienteSelect';
import { useToast } from '@/hooks/useToast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { etiquetaCoberturaJob } from '@/lib/garantia-cobertura';
import { avisosGarantiaSuelta, validarGarantiaSuelta } from '@/lib/garantia-suelta';
import { canalesWhatsApp, validarCanalDelCliente } from '@/lib/avisos-canal';
import { getContactDisplayName } from '@/lib/contact-display';
import { formatPhoneNumber } from '@/lib/format';
import { validateManualClient } from '@/lib/manual-client';
import { cn } from '@/lib/cn';

/**
 * S-03 · Abrir una GARANTÍA (tanda EMT-CNV-2026-01).
 *
 * Camino normal: se elige la instalación del cliente y la garantía HEREDA
 * cliente, modelo, serie, fecha y técnico instalador — nadie vuelve a digitar
 * nada. Al elegirla, la pantalla dice enseguida si está dentro de cobertura.
 *
 * Camino alternativo: "la instalación es anterior al sistema". Ahí no hay nada
 * que heredar, así que se piden modelo, serie y fecha a mano y la garantía
 * queda marcada para poder distinguirla después en los reportes.
 *
 * Camino SUELTO (`modo="suelta"`, botón «Abrir garantía» de la cabecera): la
 * artículo no está en el sistema y el cliente puede ni siquiera existir como
 * contacto. Se elige o se da de alta el cliente acá mismo y los datos del
 * equipo son opcionales. Existe porque sin él la oficina terminaba creando un
 * trabajo normal con la casilla "Garantía" marcada, perdiendo el plazo de
 * atención, la prioridad del cliente sin acceso y la tipificación al cerrar.
 */
type ClienteMode = 'existente' | 'nuevo';

export function NuevaGarantiaModal({
  open,
  contactId,
  contactNombre,
  instalacionInicialId,
  modo = 'instalacion',
  onClose,
}: {
  open: boolean;
  /** Cliente de la garantía. En `modo="suelta"` se elige adentro del modal. */
  contactId: string | null;
  contactNombre?: string | null;
  /**
   * Instalación pre-elegida. Cuando la garantía se abre desde la fila de un
   * trabajo, ya sabemos de cuál es: obligar a elegirla de nuevo sería pedirle
   * al operador un dato que la pantalla ya tiene.
   */
  instalacionInicialId?: string | null;
  /** 'instalacion' = desde la fila de un trabajo; 'suelta' = desde la cabecera. */
  modo?: 'instalacion' | 'suelta';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const suelta = modo === 'suelta';

  const [previa, setPrevia] = useState(false);
  // S-04 · Caso REMOTO: se atiende por teléfono, sin agendar visita.
  const [remoto, setRemoto] = useState(false);
  // S-09 · Cita obligatoria en garantías de campo y casilla de prioridad máxima.
  const [cuando, setCuando] = useState('');
  const [sinAcceso, setSinAcceso] = useState(false);
  const [esPrueba, setEsPrueba] = useState(false);
  const [instalacionJobId, setInstalacionJobId] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [modelo, setModelo] = useState('');
  const [numeroSerie, setNumeroSerie] = useState('');
  const [fechaInstalacion, setFechaInstalacion] = useState('');
  const [comentarios, setComentarios] = useState('');
  const [error, setError] = useState<string | null>(null);

  // ---- Cliente (solo en modo suelto): elegir uno o darlo de alta acá mismo.
  // Es el MISMO flujo del "Nuevo trabajo" manual, canal incluido: el canal es
  // de qué vendedora sale, y de ahí salen después los avisos de este cliente.
  const [clienteMode, setClienteMode] = useState<ClienteMode>('existente');
  const [busqueda, setBusqueda] = useState('');
  const [elegidoId, setElegidoId] = useState('');
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTelefono, setNuevoTelefono] = useState('');
  const [nuevoCanalId, setNuevoCanalId] = useState('');
  const busquedaDebounced = useDebouncedValue(busqueda, 300);

  const { data: channels } = useQuery({
    queryKey: ['channels', 'list'],
    queryFn: () => channelsApi.listChannels(),
    enabled: open && suelta && clienteMode === 'nuevo',
  });
  const canalesDelCliente = useMemo(() => canalesWhatsApp(channels ?? []), [channels]);

  const { data: encontrados, isFetching: buscando } = useQuery({
    queryKey: ['contacts', 'picker', busquedaDebounced],
    queryFn: () => contactsApi.listContacts({ search: busquedaDebounced, take: 20 }),
    enabled: open && suelta && clienteMode === 'existente' && busquedaDebounced.length >= 2,
  });

  const instalaciones = useQuery({
    queryKey: ['jobs', 'instalaciones', contactId],
    queryFn: () => jobsApi.listInstalaciones(contactId!),
    enabled: open && !suelta && Boolean(contactId),
  });
  const cfg = useQuery({
    queryKey: ['installation', 'crm'],
    queryFn: installationApi.getCrmView,
    enabled: open,
  });
  const modelos = cfg.data?.trabajos?.modelos ?? [];

  useEffect(() => {
    if (!open) return;
    setPrevia(false);
    setRemoto(false);
    setCuando('');
    setSinAcceso(false);
    setEsPrueba(false);
    setInstalacionJobId(instalacionInicialId ?? '');
    setDescripcion('');
    setModelo('');
    setNumeroSerie('');
    setFechaInstalacion('');
    setComentarios('');
    setClienteMode('existente');
    setBusqueda('');
    setElegidoId('');
    setNuevoNombre('');
    setNuevoTelefono('');
    setNuevoCanalId('');
    setError(null);
  }, [open, contactId, instalacionInicialId]);

  const elegida = (instalaciones.data ?? []).find((i) => i.id === instalacionJobId);
  // Aviso de cobertura de la instalación elegida: el operador tiene que saber
  // ANTES de agendar si esto se cobra o no.
  const cobertura = elegida
    ? etiquetaCoberturaJob(
        { modelo: elegida.modelo, fechaInstalacion: elegida.completedAt },
        modelos,
      )
    : null;

  const formularioSuelto = {
    descripcion,
    cuando,
    remoto,
    previa,
    modelo,
    numeroSerie,
    fechaInstalacion,
  };
  const avisos = suelta ? avisosGarantiaSuelta(formularioSuelto) : [];

  const crear = useMutation({
    mutationFn: async () => {
      // Modo suelto: primero hay que tener el cliente. El alta manual devuelve
      // el existente si el teléfono ya estaba — nunca se duplica un contacto.
      let cliente = contactId ?? '';
      if (suelta) {
        if (clienteMode === 'nuevo') {
          const { contact, created } = await contactsApi.createManualContact({
            nombre: nuevoNombre,
            telefono: nuevoTelefono,
            channelId: nuevoCanalId || null,
          });
          cliente = contact.id;
          void qc.invalidateQueries({ queryKey: ['contacts'] });
          if (!created) toast.info('Ese teléfono ya existía: se usó el cliente existente');
        } else {
          cliente = elegidoId;
        }
      }
      const cita = remoto || !cuando ? null : new Date(cuando).toISOString();
      if (suelta) {
        return jobsApi.crearGarantia({
          // Marcar "anterior al sistema" es decir que SÍ se tienen los datos:
          // ahí vale el camino estricto de siempre.
          ...(previa ? { instalacionPrevia: true } : { garantiaSuelta: true }),
          contactId: cliente,
          descripcion: descripcion.trim(),
          modelo: modelo.trim() || null,
          numeroSerie: numeroSerie.trim() || null,
          fechaInstalacion: fechaInstalacion || null,
          notas: comentarios.trim() || null,
          esRemoto: remoto,
          clienteSinAcceso: sinAcceso,
          esPrueba,
          scheduledAt: cita,
        });
      }
      return jobsApi.crearGarantia(
        previa
          ? {
              instalacionPrevia: true,
              contactId: contactId!,
              descripcion: descripcion.trim(),
              modelo: modelo.trim(),
              numeroSerie: numeroSerie.trim(),
              fechaInstalacion: fechaInstalacion || null,
              esRemoto: remoto,
              clienteSinAcceso: sinAcceso,
              esPrueba,
              scheduledAt: cita,
            }
          : {
              instalacionJobId,
              descripcion: descripcion.trim(),
              esRemoto: remoto,
              clienteSinAcceso: sinAcceso,
              esPrueba,
              scheduledAt: cita,
            },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success(remoto ? 'Caso abierto para atender por teléfono' : 'Garantía abierta');
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo abrir la garantía'),
  });

  function submit() {
    if (suelta) {
      if (clienteMode === 'existente' && !elegidoId) {
        setError('Elija el cliente de la garantía (o dé de alta uno nuevo).');
        return;
      }
      if (clienteMode === 'nuevo') {
        const v = validateManualClient({ nombre: nuevoNombre, telefono: nuevoTelefono });
        if (!v.ok) {
          setError(v.error);
          return;
        }
        const canal = validarCanalDelCliente(canalesDelCliente, nuevoCanalId);
        if (!canal.ok) {
          setError(canal.error);
          return;
        }
      }
      const falta = validarGarantiaSuelta(formularioSuelto);
      if (falta) {
        setError(falta);
        return;
      }
      setError(null);
      crear.mutate();
      return;
    }

    if (!descripcion.trim()) {
      setError('Describa el problema que reporta el cliente.');
      return;
    }
    if (!previa && !instalacionJobId) {
      setError('Elija de qué instalación es esta garantía.');
      return;
    }
    if (previa && (!modelo.trim() || !numeroSerie.trim() || !fechaInstalacion)) {
      setError('Para una instalación anterior al sistema hacen falta modelo, serie y fecha.');
      return;
    }
    // S-09: hoy el 74% de las garantías no tiene cita. Se pide siempre, salvo
    // en los casos remotos, que por definición no llevan visita.
    if (!remoto && !cuando) {
      setError('Indique el día y la hora en que se va a atender la garantía.');
      return;
    }
    setError(null);
    crear.mutate();
  }

  const sinInstalaciones = (instalaciones.data ?? []).length === 0 && !instalaciones.isLoading;
  // Los datos del equipo se piden a mano en los dos casos en que no hay de
  // dónde copiarlos: la instalación previa y la garantía suelta.
  const pideDatosDelEquipo = suelta || previa;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Abrir una garantía"
      description={
        suelta
          ? 'Para un artículo que no está en el sistema. Elija el cliente y cargue lo que sepa del equipo.'
          : contactNombre
            ? `Garantía de ${contactNombre}. Se toman los datos de su instalación.`
            : 'Se toman los datos de la instalación del cliente.'
      }
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button data-guia="garantia.guardar" onClick={submit} loading={crear.isPending}>
            <ShieldAlert className="mr-1 h-4 w-4" />
            Abrir garantía
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {suelta && (
          <section className="space-y-2 rounded-md border border-surface-border p-3 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Cliente</p>
              <div className="flex gap-1">
                {(
                  [
                    { value: 'existente', label: 'Existente' },
                    { value: 'nuevo', label: 'Nuevo' },
                  ] as { value: ClienteMode; label: string }[]
                ).map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setClienteMode(m.value)}
                    className={cn(
                      'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                      clienteMode === m.value
                        ? 'bg-brand-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
                    )}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {clienteMode === 'existente' ? (
              <>
                <Input
                  data-guia="garantia.cliente"
                  label="Buscar cliente"
                  placeholder="Nombre o teléfono…"
                  value={busqueda}
                  onChange={(e) => {
                    setBusqueda(e.target.value);
                    setElegidoId('');
                  }}
                />
                {busquedaDebounced.length >= 2 && (
                  <div className="max-h-40 space-y-1 overflow-y-auto">
                    {buscando ? (
                      <p className="p-1 text-xs text-slate-400">Buscando…</p>
                    ) : (encontrados?.items ?? []).length === 0 ? (
                      <p className="p-1 text-xs text-slate-400">
                        Sin resultados — puede darlo de alta con la pestaña «Nuevo».
                      </p>
                    ) : (
                      (encontrados?.items ?? []).map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setElegidoId(c.id)}
                          className={cn(
                            'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                            elegidoId === c.id
                              ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                          )}
                        >
                          <span className="truncate">{getContactDisplayName(c)}</span>
                          <span className="ml-2 shrink-0 text-xs text-slate-400">
                            {c.phone ? formatPhoneNumber(c.phone) : 'sin número'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Nombre"
                    placeholder="Nombre del cliente"
                    value={nuevoNombre}
                    onChange={(e) => setNuevoNombre(e.target.value)}
                  />
                  <Input
                    label="Teléfono"
                    placeholder="18095550000"
                    value={nuevoTelefono}
                    onChange={(e) => setNuevoTelefono(e.target.value)}
                  />
                </div>
                <CanalDelClienteSelect
                  canales={canalesDelCliente}
                  value={nuevoCanalId}
                  onChange={setNuevoCanalId}
                />
              </div>
            )}
          </section>
        )}

        <Textarea
          data-guia="garantia.que-reporta"
          label="¿Qué reporta el cliente?"
          placeholder="No abre con huella desde ayer"
          rows={2}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
        />

        {!suelta && !previa && (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              ¿De qué instalación es?
            </span>
            <select
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              value={instalacionJobId}
              onChange={(e) => setInstalacionJobId(e.target.value)}
            >
              <option value="">Elija la instalación…</option>
              {(instalaciones.data ?? []).map((i) => (
                <option key={i.id} value={i.id}>
                  {i.completedAt ? new Date(i.completedAt).toLocaleDateString() : 'sin fecha'} ·{' '}
                  {i.modelo ?? 'sin modelo'} · {i.descripcion.slice(0, 40)}
                </option>
              ))}
            </select>
            {sinInstalaciones && (
              <span className="mt-1 block text-xs text-slate-500">
                Este cliente no tiene instalaciones cerradas en el sistema. Si el artículo se
                instaló antes, marque la opción de abajo.
              </span>
            )}
          </label>
        )}

        {pideDatosDelEquipo && (
          <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50/60 p-3">
            <p className="text-xs text-amber-900">
              {previa
                ? 'La instalación es anterior al sistema: no hay de dónde copiar los datos, así que hay que cargarlos a mano. La garantía queda marcada como tal.'
                : 'Esta artículo no está en el sistema. Cargue lo que sepa del equipo; si no sabe nada, déjelo vacío y la garantía se abre igual.'}
            </p>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                Modelo instalado
              </span>
              <select
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={modelo}
                onChange={(e) => setModelo(e.target.value)}
              >
                <option value="">Elija el modelo…</option>
                {modelos.map((m) => (
                  <option key={m.id} value={m.nombre}>
                    {m.nombre}
                  </option>
                ))}
              </select>
            </label>
            <Input
              label="Número de serie"
              value={numeroSerie}
              onChange={(e) => setNumeroSerie(e.target.value)}
            />
            <Input
              label="Fecha de instalación"
              type="date"
              value={fechaInstalacion}
              onChange={(e) => setFechaInstalacion(e.target.value)}
            />
            {/* Avisar no es prohibir: se dice qué se pierde y el botón sigue
                funcionando. Si el formulario trabara, la oficina volvería al
                atajo de abrir un trabajo normal marcando "Garantía". */}
            {avisos.map((a) => (
              <p key={a} className="text-xs font-medium text-amber-800">
                {a}
              </p>
            ))}
          </div>
        )}

        {cobertura && (
          <p
            className={
              cobertura.tono === 'ok'
                ? 'rounded-md bg-green-50 p-3 text-sm text-green-800'
                : cobertura.tono === 'fuera'
                  ? 'rounded-md bg-red-50 p-3 text-sm text-red-800'
                  : 'rounded-md bg-slate-100 p-3 text-sm text-slate-700'
            }
          >
            {cobertura.texto}
            {/* El aviso decía qué faltaba y dejaba al operador a pie: tenía que
                acordarse de dónde se cargan los meses de cobertura, salir de la
                garantía que estaba abriendo e ir a buscarlo. El atajo lo lleva
                derecho. Solo aparece cuando lo que falta es el PERÍODO — si lo
                que falta es la fecha, esa pantalla no arregla nada. */}
            {cobertura.faltaPeriodoDelModelo && (
              <Link
                to="/installation"
                className="mt-1 block text-xs font-semibold underline underline-offset-2"
                title="Abre Mi negocio, donde en la pestaña Trabajos se carga el período de cada modelo"
              >
                Cargar los meses
              </Link>
            )}
            {elegida?.tecnicoNombre && (
              <span className="mt-1 block text-xs">Instaló: {elegida.tecnicoNombre}</span>
            )}
          </p>
        )}

        {!remoto && (
          <Input
            label="¿Cuándo se atiende?"
            type="datetime-local"
            value={cuando}
            onChange={(e) => setCuando(e.target.value)}
          />
        )}

        <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            checked={sinAcceso}
            onChange={(e) => setSinAcceso(e.target.checked)}
          />
          <span>
            El cliente no puede entrar a su vivienda
            <span className="block text-xs text-slate-500">
              Pasa al primer lugar de la lista, por encima de cualquier otra garantía.
            </span>
          </span>
        </label>

        {/* S-04 · Un caso remoto no agenda visita ni ocupa técnico. Va acá, en
            el mismo modal, y no en una pantalla aparte: quien atiende al
            cliente no debería tener que decidir a qué pantalla ir ANTES de
            saber si el problema se resuelve por teléfono. */}
        <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            checked={remoto}
            onChange={(e) => setRemoto(e.target.checked)}
          />
          <span>
            Se atiende por teléfono, sin ir al sitio
            <span className="block text-xs text-slate-500">
              No ocupa técnico ni agenda. Si después hay que ir, desde el caso se manda un técnico
              sin perder nada de lo registrado.
            </span>
          </span>
        </label>

        <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            checked={previa}
            onChange={(e) => {
              setPrevia(e.target.checked);
              setInstalacionJobId('');
              setError(null);
            }}
          />
          <span>
            El artículo se instaló antes de que existiera el sistema
            {suelta && (
              <span className="block text-xs text-slate-500">
                Márquelo solo si tiene el modelo, la serie y la fecha: con esos tres se puede
                calcular la cobertura.
              </span>
            )}
          </span>
        </label>

        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-dashed border-slate-300 p-2 text-sm text-slate-600">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            checked={esPrueba}
            onChange={(e) => setEsPrueba(e.target.checked)}
          />
          <span>
            Es un registro de prueba
            <span className="block text-xs text-slate-500">
              Si la instalación de origen ya es de prueba, esta garantía lo será igual aunque no lo
              marque.
            </span>
          </span>
        </label>

        {suelta && (
          <Textarea
            label="Comentarios"
            placeholder="Lo que haga falta que sepa el técnico antes de ir"
            rows={2}
            value={comentarios}
            onChange={(e) => setComentarios(e.target.value)}
          />
        )}

        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
